/**
 * Product catalog – đồng bộ với hệ thống EPOS (DOCS/EPOS/screenshots/01_lua_chon_san_pham)
 * Mỗi sản phẩm có 4 icon mục tiêu tài chính (life-protection, education, retirement, savings)
 *
 * Mỗi sản phẩm có thể có `productConfig` chi tiết cho form input
 * (xem ProductConfig bên dưới).
 */

export type ProductTarget = 'life-protection' | 'education' | 'retirement' | 'savings';
export type ProductType = 'linked-unit' | 'linked-general';

export interface ProductConfig {
  /** Phí cơ bản định kỳ tối thiểu (VND/năm) */
  minBasePremium: number;
  /** Phí cơ bản định kỳ mặc định */
  defaultBasePremium: number;
  /** Số tiền bảo hiểm (STBH) tối thiểu */
  minSumAssured: number;
  /** Số tiền bảo hiểm tối đa */
  maxSumAssured: number;
  /** STBH mặc định */
  defaultSumAssured: number;
  /** Số năm đóng phí tối thiểu */
  minTerm: number;
  /** Số năm đóng phí tối đa */
  maxTerm: number;
  /** Số năm đóng phí mặc định */
  defaultTerm: number;
  /** Tuổi đóng phí tối đa (NĐBH) */
  maxAgeAtMaturity: number;
  /** Tuổi tối thiểu được mua */
  minAge: number;
  /** Các quỹ đầu tư khả dụng */
  availableFunds: string[];
  /** Phân bổ quỹ mặc định (% cho mỗi quỹ) */
  defaultFundAllocation: Record<string, number>;
  /** Cấu hình đầu tư thêm (Top-up) */
  topUp: {
    enabled: boolean;
    minAmount: number;
    maxAmount: number;
    defaultAmount: number;
    defaultStartYear: number;
    defaultDuration: number;
    maxDuration: number;
  };
  /** Chu kỳ đóng phí khả dụng */
  paymentFrequencies: string[];
}

export interface Product {
  id: string;
  name: string;
  fullName: string;
  type: ProductType;
  typeLabel: string;
  tagline: string;
  targets: ProductTarget[];
  features: string[];
  requireRiskAssessment: boolean;
  isDefault?: boolean;
  config?: ProductConfig;
}

/**
 * Cấu hình chi tiết cho Xanh Ước Mơ (XUM)
 * Đối chiếu với Curex screenshot 9_input/1_xanh_uoc_mo_sp_chinh_edit.png
 */
const XUM_CONFIG: ProductConfig = {
  minBasePremium: 5_000_000,        // 5 triệu
  defaultBasePremium: 10_000_000,
  minSumAssured: 500_000_000,       // 500 triệu
  maxSumAssured: 900_000_000,       // 900 triệu
  defaultSumAssured: 500_000_000,
  minTerm: 10,
  maxTerm: 20,
  defaultTerm: 20,
  maxAgeAtMaturity: 65,             // Đóng phí tối đa đến 65 tuổi
  minAge: 18,
  availableFunds: ['Quỹ Liên Kết Chung'],
  defaultFundAllocation: { 'Quỹ Liên Kết Chung': 100 },
  topUp: {
    enabled: true,
    minAmount: 0,
    maxAmount: 1_000_000_000,       // 1 tỷ/năm
    defaultAmount: 0,
    defaultStartYear: 1,
    defaultDuration: 0,
    maxDuration: 20,
  },
  paymentFrequencies: ['Hàng năm', 'Nửa năm', 'Quý', 'Hàng tháng'],
};

/**
 * Cấu hình chi tiết cho Xanh Phú Quý (XPQ) - Liên kết đơn vị (Unit-Linked)
 */
const XPQ_CONFIG: ProductConfig = {
  minBasePremium: 15_000_000,       // 15 triệu
  defaultBasePremium: 20_000_000,
  minSumAssured: 500_000_000,       // 500 triệu
  maxSumAssured: 5_000_000_000,     // 5 tỷ
  defaultSumAssured: 1_000_000_000,
  minTerm: 10,
  maxTerm: 20,
  defaultTerm: 20,
  maxAgeAtMaturity: 65,
  minAge: 18,
  availableFunds: [
    'Quỹ Tăng Trưởng (Growth Fund)',
    'Quỹ Cân Bằng (Balanced Fund)',
    'Quỹ Bảo Toàn (Preservation Fund)',
    'Quỹ Hưng Thịnh 2035',
    'Quỹ Hưng Thịnh 2040',
  ],
  defaultFundAllocation: {
    'Quỹ Tăng Trưởng (Growth Fund)': 50,
    'Quỹ Cân Bằng (Balanced Fund)': 30,
    'Quỹ Bảo Toàn (Preservation Fund)': 20,
  },
  topUp: {
    enabled: true,
    minAmount: 0,
    maxAmount: 1_500_000_000,
    defaultAmount: 0,
    defaultStartYear: 1,
    defaultDuration: 0,
    maxDuration: 20,
  },
  paymentFrequencies: ['Hàng năm', 'Nửa năm', 'Quý'],
};

/**
 * Cấu hình chi tiết cho Xanh Tương Lai (XTL) - 3 Cộng+
 */
const XTL_CONFIG: ProductConfig = {
  minBasePremium: 10_000_000,       // 10 triệu
  defaultBasePremium: 15_000_000,
  minSumAssured: 300_000_000,       // 300 triệu
  maxSumAssured: 3_000_000_000,     // 3 tỷ
  defaultSumAssured: 500_000_000,
  minTerm: 10,
  maxTerm: 20,
  defaultTerm: 20,
  maxAgeAtMaturity: 65,
  minAge: 0,
  availableFunds: [
    'Quỹ Tăng Trưởng',
    'Quỹ Cân Bằng',
    'Quỹ Bảo Toàn',
    'Quỹ Trái Phiếu',
  ],
  defaultFundAllocation: {
    'Quỹ Tăng Trưởng': 40,
    'Quỹ Cân Bằng': 40,
    'Quỹ Bảo Toàn': 20,
  },
  topUp: {
    enabled: true,
    minAmount: 0,
    maxAmount: 1_000_000_000,
    defaultAmount: 0,
    defaultStartYear: 1,
    defaultDuration: 0,
    maxDuration: 20,
  },
  paymentFrequencies: ['Hàng năm', 'Nửa năm', 'Quý', 'Hàng tháng'],
};

/**
 * Cấu hình chi tiết cho Xanh Tương Lai Phí Ổn Định (XTL-PS)
 */
const XTL_PS_CONFIG: ProductConfig = {
  ...XTL_CONFIG,
  minBasePremium: 12_000_000,
  defaultBasePremium: 15_000_000,
};

export const PRODUCTS: Product[] = [
  {
    id: 'XUM',
    name: 'Xanh Ước Mơ',
    fullName: 'Sản phẩm Bảo hiểm Liên kết chung Xanh Ước Mơ',
    type: 'linked-general',
    typeLabel: 'Liên kết chung',
    tagline: 'Giải pháp tích lũy giáo dục 1 2 3',
    targets: ['life-protection', 'education', 'retirement', 'savings'],
    features: [
      'Bảo vệ trọn đời trước các rủi ro trong cuộc sống',
      'Tích luỹ hiệu quả với lãi suất cam kết và lãi suất gia tăng',
      'Thưởng duy trì hợp đồng tại cột mốc kỷ niệm năm thứ 10',
      'Gia tăng STBH khi có thành viên mới trong gia đình',
    ],
    requireRiskAssessment: false,
    isDefault: true,
    config: XUM_CONFIG,
  },
  {
    id: 'XPQ',
    name: 'Xanh Phú Quý',
    fullName: 'Sản phẩm Bảo hiểm Liên kết đơn vị Xanh Phú Quý',
    type: 'linked-unit',
    typeLabel: 'Liên kết đơn vị',
    tagline: 'Kiến Tạo Gia Sản',
    targets: ['life-protection', 'education', 'retirement', 'savings'],
    features: [
      'Kiến tạo gia sản với danh mục 9 quỹ đơn vị chuyên nghiệp do Manulife Investment điều hành',
      'Tái phân bổ danh mục đầu tư linh hoạt (Rebalancing) theo khẩu vị rủi ro',
      'Bảo vệ sinh mạng với quyền lợi cao [STBH + Giá trị tài khoản quỹ]',
      'Thưởng gia tăng duy trì hợp đồng tại năm 10, 15, 20',
    ],
    requireRiskAssessment: true,
    config: XPQ_CONFIG,
  },
  {
    id: 'XTL-PS',
    name: 'Xanh Tương Lai (Phí Ổn Định)',
    fullName: 'Sản phẩm Bảo hiểm Liên kết đơn vị Xanh Tương Lai (Phiên bản phí ổn định)',
    type: 'linked-unit',
    typeLabel: 'Liên kết đơn vị',
    tagline: 'Giải pháp Tài Chính Đa Mục Tiêu',
    targets: ['life-protection', 'education', 'retirement', 'savings'],
    features: [
      'An tâm hoạch định với tổng phí bảo hiểm ổn định hàng năm',
      'Bảo vệ tối ưu với quyền lợi gia tăng STBH lên đến 175% STBH ban đầu',
      'Xây dựng quỹ tài chính cho tương lai hưng thịnh với 9 Quỹ liên kết đơn vị',
    ],
    requireRiskAssessment: true,
    config: XTL_PS_CONFIG,
  },
  {
    id: 'XTL',
    name: 'Xanh Tương Lai',
    fullName: 'Sản phẩm Bảo hiểm Liên kết đơn vị Xanh Tương Lai',
    type: 'linked-unit',
    typeLabel: 'Liên kết đơn vị',
    tagline: 'Giải pháp tài chính đa mục tiêu 3 Cộng (+)',
    targets: ['life-protection', 'education', 'retirement', 'savings'],
    features: [
      'Bình an trọn vẹn trước các rủi ro về sinh mạng và sức khỏe',
      'Tự động gia tăng STBH từ Năm Hợp Đồng 02 đến 06 (lên đến 175%)',
      'Gia tăng STBH khi có thành viên mới trong gia đình',
      'Xây dựng tương lai hưng thịnh với danh mục quỹ liên kết đơn vị ưu việt',
    ],
    requireRiskAssessment: true,
    config: XTL_CONFIG,
  },
];

export const findProductById = (id: string): Product | undefined =>
  PRODUCTS.find((p) => p.id === id);

